import { useState } from 'react';
import type { ProductMix, ProductMixSlice, Provenance } from '@losan/core';
import { DemoBadge } from './DemoBadge.js';

const eurFmt = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const eurFmtCompact = new Intl.NumberFormat('es-ES', { notation: 'compact', style: 'currency', currency: 'EUR', maximumFractionDigits: 1 });
const intFmt = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });
const intCompact = new Intl.NumberFormat('es-ES', { notation: 'compact', maximumFractionDigits: 1 });

// Stable color per line across the three donuts.
const PALETTE: Record<string, string> = {
  MAN: '#1e3a8a',
  WOMAN: '#be185d',
  TEEN: '#0d9488',
  KIDS: '#ca8a04',
  BABY: '#0891b2',
  NEWBORN: '#7c3aed',
  DORIA: '#6b7280',
};
const FALLBACK_PALETTE = ['#1e3a8a', '#3b82f6', '#0ea5e9', '#0891b2', '#0d9488', '#65a30d', '#ca8a04', '#c2410c', '#be185d', '#6b7280'];
const colorFor = (key: string, idx: number): string =>
  PALETTE[key] ?? FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length]!;

type Metric = 'revenue' | 'units' | 'clients';
const METRIC_OPTIONS: { id: Metric; label: string }[] = [
  { id: 'revenue', label: 'Facturación' },
  { id: 'units', label: 'Unidades' },
  { id: 'clients', label: 'Clientes' },
];

function metricValue(slice: ProductMixSlice, metric: Metric): number {
  if (metric === 'revenue') return slice.revenueEUR;
  if (metric === 'units') return slice.units;
  return slice.clients;
}
function formatValue(value: number, metric: Metric): string {
  if (metric === 'revenue') return eurFmt.format(value);
  return intFmt.format(value);
}
function formatValueCompact(value: number, metric: Metric): string {
  if (metric === 'revenue') return eurFmtCompact.format(value);
  return intCompact.format(value);
}

interface Props {
  mix: ProductMix;
  provenance: Provenance;
  currentSeason: string;
  previousSeasonLabel: string | null;
}

export function ProductMixPanel({ mix, provenance, currentSeason, previousSeasonLabel }: Props) {
  const [metric, setMetric] = useState<Metric>('revenue');

  // Stable color assignment across the three donuts.
  const allKeys = new Set<string>();
  for (const s of [...mix.byLineCurrent, ...mix.byLineLastSeason, ...mix.byLineTarget]) {
    if (s.key) allKeys.add(s.key);
  }
  const keyToIdx = new Map<string, number>();
  Array.from(allKeys)
    .sort((a, b) => keyTotal(mix, b, metric) - keyTotal(mix, a, metric))
    .forEach((k, i) => keyToIdx.set(k, i));

  return (
    <div className="section product-mix">
      <div className="product-mix-head">
        <h2>
          Mix por línea <DemoBadge provenance={provenance} />
        </h2>
        <div className="metric-toggle" role="tablist" aria-label="Métrica del mix">
          {METRIC_OPTIONS.map((o) => (
            <button
              key={o.id}
              role="tab"
              aria-selected={metric === o.id}
              className={metric === o.id ? 'active' : ''}
              onClick={() => setMetric(o.id)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="donut-grid">
        <DonutBlock title={currentSeason} subtitle="Esta temporada" slices={mix.byLineCurrent} metric={metric} colorIdx={keyToIdx} />
        <DonutBlock title={previousSeasonLabel ?? '—'} subtitle="Misma temporada N-1" slices={mix.byLineLastSeason} metric={metric} colorIdx={keyToIdx} mutedIfEmpty />
        <DonutBlock title="Objetivo" subtitle={`${currentSeason} target`} slices={mix.byLineTarget} metric={metric} colorIdx={keyToIdx} />
      </div>
    </div>
  );
}

function keyTotal(mix: ProductMix, key: string, metric: Metric): number {
  const sum = (slices: ProductMixSlice[]) =>
    slices.filter((s) => s.key === key).reduce((acc, s) => acc + metricValue(s, metric), 0);
  return sum(mix.byLineCurrent) + sum(mix.byLineLastSeason) + sum(mix.byLineTarget);
}

interface DonutBlockProps {
  title: string;
  subtitle: string;
  slices: ProductMixSlice[];
  metric: Metric;
  colorIdx: Map<string, number>;
  mutedIfEmpty?: boolean;
}

function DonutBlock({ title, subtitle, slices, metric, colorIdx, mutedIfEmpty }: DonutBlockProps) {
  const total = slices.reduce((s, x) => s + metricValue(x, metric), 0);
  const empty = slices.length === 0 || total <= 0;
  return (
    <div className={`donut-block ${empty && mutedIfEmpty ? 'muted' : ''}`}>
      <div className="donut-block-head">
        <h3>{title}</h3>
        <div className="donut-block-sub">{subtitle}</div>
      </div>
      {empty ? (
        <div className="donut-empty">Sin datos para este corte</div>
      ) : (
        <>
          <Donut slices={slices} total={total} metric={metric} colorIdx={colorIdx} />
          <div className="donut-total">{formatValue(total, metric)}</div>
        </>
      )}
    </div>
  );
}

/**
 * SVG donut with inline data labels next to each slice.
 * Labels sit just outside the ring at the angular midpoint of each arc.
 * Slices below 5% of the total don't render a label (would overlap with
 * neighbours); they're still represented in the ring and the tooltip.
 */
function Donut({ slices, total, metric, colorIdx }: { slices: ProductMixSlice[]; total: number; metric: Metric; colorIdx: Map<string, number> }) {
  const size = 240;
  const radius = 78;
  const stroke = 22;
  const c = size / 2;
  const circ = 2 * Math.PI * radius;
  const labelOffset = stroke / 2 + 6; // gap between ring outer edge and label
  const LABEL_THRESHOLD = 0.05;

  let acc = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="donut-svg" role="img" aria-label="Reparto por línea">
      <circle cx={c} cy={c} r={radius} fill="none" stroke="#eef0f4" strokeWidth={stroke} />
      {slices.map((s) => {
        const value = metricValue(s, metric);
        const frac = value / total;
        const len = frac * circ;
        const off = (acc / total) * circ;
        acc += value;
        const color = colorFor(s.key, colorIdx.get(s.key) ?? 0);
        return (
          <circle
            key={`arc-${s.key}`}
            cx={c}
            cy={c}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={`${len} ${circ - len}`}
            strokeDashoffset={-off}
            transform={`rotate(-90 ${c} ${c})`}
            style={{ transition: 'stroke-dasharray 0.4s ease, stroke-dashoffset 0.4s ease' }}
          >
            <title>{`${s.key}: ${formatValue(value, metric)} (${(frac * 100).toFixed(1)}%)`}</title>
          </circle>
        );
      })}
      {/* Labels positioned at the angular midpoint of each slice. */}
      {(() => {
        let acc2 = 0;
        return slices.map((s) => {
          const value = metricValue(s, metric);
          const frac = value / total;
          const midFrac = (acc2 + value / 2) / total;
          acc2 += value;
          if (frac < LABEL_THRESHOLD) return null;
          const angle = midFrac * 2 * Math.PI - Math.PI / 2;
          const lx = c + Math.cos(angle) * (radius + labelOffset);
          const ly = c + Math.sin(angle) * (radius + labelOffset);
          const anchor = Math.cos(angle) > 0.15 ? 'start' : Math.cos(angle) < -0.15 ? 'end' : 'middle';
          return (
            <g key={`lbl-${s.key}`} style={{ pointerEvents: 'none' }}>
              <text x={lx} y={ly} textAnchor={anchor} className="donut-label-name" dominantBaseline="middle">
                {s.key || '—'}
              </text>
              <text x={lx} y={ly + 12} textAnchor={anchor} className="donut-label-value" dominantBaseline="middle">
                {formatValueCompact(value, metric)} · {(frac * 100).toFixed(0)}%
              </text>
            </g>
          );
        });
      })()}
    </svg>
  );
}
